import tw, { styled } from "twin.macro";
import React, { useState, useMemo, useCallback } from "react";

import { GiftCardForm, type GiftCardFormProps } from "@bared/ui";

import RichText from "../RichText";
import ModalsPopup from "@components/Modals/ModalsPopup";
import FormDropdown from "@components/Form/FormDropdown";
import { StyledButton } from "@components/Styled/Button";
import { Breadcrumb } from "@components/Breadcrumb/Breadcrumb";
import { BackgroundContainer } from "@components/Styled/Container";

import { Images } from "./Images/Images";
import { Container, Content, Desktop, Mobile, Sticky } from "./Shared";

import { withProduct } from "./withProduct";

import { useCartAdd } from "~/hooks/useCart";

const VariantSelector = tw(FormDropdown)`
  w-full block text-body leading-input mb-mini
`;

const Title = tw.h1`
  text-h2 leading-h1 mb-mini font-display md:text-[56px]
`;

const Description = styled(RichText)`
  ${tw`block mb-mini`}
  p {
    ${tw`text-left`}
  }
`;

const BuyNow = tw(StyledButton)`
  w-full px-mini my-mini
`;

export const GiftCard = withProduct(
  ({
    badge,
    description,
    dropdownOptions,
    handleChange,
    handleSlideChange,
    heroImage,
    images,
    paths,
    selectedVariant,
    productName,
    video,
    product,
  }) => {
    const [modalActive, setModalActive] = useState(false);

    const variants = useMemo<GiftCardFormProps["variants"]>(
      () =>
        product.variants?.map((variant) => ({
          // Sometimes the variant ID is base 64 encoded ¯\_(ツ)_/¯
          id: variant.id.includes("=") ? atob(variant.id) : variant.id,
          price: variant.priceV2,
        })) ?? [],
      [product],
    );

    const { addToCart } = useCartAdd();

    const onSubmit = useCallback<GiftCardFormProps["onSubmit"]>(
      async (data) => {
        await addToCart({
          quantity: 1,
          merchandiseId: data.merchandiseId,
          attributes: [
            {
              key: "From",
              value: data.senderName,
            },
            {
              key: "Recipient email",
              value: data.recipientEmail,
            },
            {
              key: "Recipient name",
              value: data.recipientName,
            },
            {
              key: "Send on",
              value: data.sendDate,
            },
            {
              key: "__shopify_send_gift_card_to_recipient",
              value: "true",
            },
            ...(!!data.message
              ? [
                  {
                    key: "Message",
                    value: data.message,
                  },
                ]
              : []),
            ...(product.image
              ? [
                  {
                    key: "_image",
                    value: product.image,
                  },
                ]
              : []),
          ],
        });

        setModalActive(false);
      },
      [addToCart],
    );

    return (
      <BackgroundContainer background="light">
        <Desktop>
          <Breadcrumb paths={paths} float />
        </Desktop>
        <Container width="full">
          <Sticky>
            <Images
              image={heroImage}
              images={images}
              video={video}
              badge={badge}
              onSlideChange={handleSlideChange}
            />
          </Sticky>
          <Content>
            <Mobile>
              <Breadcrumb paths={paths} float />
            </Mobile>
            <Title>{productName}</Title>
            <VariantSelector
              placeholder="Select an amount"
              name="Size selector"
              layout="denominations"
              options={dropdownOptions}
              handleChange={handleChange}
            />
            <BuyNow
              height="56"
              textSize="16"
              colour="secondary"
              onClick={() => setModalActive(true)}
            >
              Add to Cart
            </BuyNow>
            <Description html={description} />
            <ModalsPopup
              layout="gift-card"
              active={modalActive}
              setActive={setModalActive}
            >
              {modalActive && (
                <GiftCardForm
                  variants={variants}
                  onSubmit={onSubmit}
                  className="w-full p-midi"
                  initialMerchandiseId={selectedVariant?.id}
                />
              )}
            </ModalsPopup>
          </Content>
        </Container>
      </BackgroundContainer>
    );
  },
);
